import WebViewer from '@pdftron/pdfjs-express-viewer';
import { Box, IconButton, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import SplitPDF from './toolbar/SplitPDF';



Viewer.propTypes = {
    blobUrl: PropTypes.string,
    onDownload: PropTypes.func,
    fileName: PropTypes.string,
    highlightRegion: PropTypes.object,
    splitPDF: PropTypes.bool,
    handleDelete: PropTypes.func
};

export default function Viewer({ blobUrl, onDownload, fileName, highlightRegion, splitPDF = true, handleDelete = null }) {
    const viewer = useRef(null);
    const [showSplitDialog, setShowSplitDialog] = useState(false);
    const viewerPropsRef = useRef({
        blobUrl: undefined,
        fileName: undefined,
        onDownload: undefined,
        handleDelete: undefined,
    });
    useEffect(() => {
        viewerPropsRef.current = { blobUrl, fileName, onDownload, handleDelete };
    }, [blobUrl, fileName, onDownload, handleDelete]);

    const customDownloadButton = {
        type: 'actionButton',
        img: '/assets/downloadIcon.svg',
        onClick: () => {
            handleDownload();
        },
        dataElement: 'alertButton',
    };

    const customSplitPDFButton = {
        type: 'actionButton',
        img: '/assets/callSplitIcon.svg',
        onClick: () => setShowSplitDialog(true),
        title: 'Split PDF',
    };

    const customDeleteButton = {
        type: 'actionButton',
        img: '/assets/trashIcon.svg',
        onClick: () => viewerPropsRef.current.handleDelete?.(),
        title: 'Delete',
    };

    const customOpenInNewTabButton = {
        type: 'actionButton',
        img: '/assets/openInNewTab.svg',
        onClick: () => {
            const { blobUrl: currentBlobUrl } = viewerPropsRef.current;
            if (currentBlobUrl) window.open(currentBlobUrl);
        },
        title: 'Open in New Tab',
    };

    const customRotateLeftButton = {
        type: 'actionButton',
        img: '/assets/rotateLeft.svg',
        onClick: () => {
            if (viewer.current?.instance) {
                const { Core } = viewer.current.instance;
                Core.documentViewer.rotateCounterClockwise();
            }
        },
        title: 'Rotate Left',
    };

    const customRotateRightButton = {
        type: 'actionButton',
        img: '/assets/rotateRight.svg',
        onClick: () => {
            if (viewer.current?.instance) {
                const { Core } = viewer.current.instance;
                Core.documentViewer.rotateClockwise();
            }
        },
        title: 'Rotate Right',
    };

    useEffect(() => {
        let instance;
        WebViewer(
            {
                path: '/pdftron',
                initialDoc: blobUrl,
                licenseKey: 'c0O4G4laBiOLZ7Z3MzE4',
                css: 'PS-Backend/ClientApp/src/reusable-components/pdf-viewer/pdfjs_css.css',
                extension: 'pdf',
            },
            viewer.current
        ).then((webViewerInstance) => {
            instance = webViewerInstance;
            viewer.current.instance = instance;
            instance.UI.disableElements([
                'contextMenuPopup',
                'selectToolButton',
                'panToolButton',
                'rotateClockwiseButton',
                'rotateCounterClockwiseButton',
                'viewControlsDivider1',
                'rotateHeader',
            ]);
            instance.UI.setHeaderItems((header) => {
                const items = header?.getItems?.() ?? [];
                const insertItems = [
                    handleDelete ? customDeleteButton : null,
                    customRotateLeftButton,
                    customRotateRightButton,
                    customDownloadButton,
                    splitPDF ? customSplitPDFButton : null,
                    customOpenInNewTabButton,
                ].filter((item) => item != null);

                const insertIndex = Math.max(items.length - 2, 0);
                items.splice(insertIndex, 0, ...insertItems);
                header?.update?.(items);
            });
            instance.disableElements(['downloadButton']);
            instance.disableElements(['themeChangeButton']);
            instance.disableElements(['languageButton']);
            instance.setFitMode(instance.FitMode.FitWidth);
            const iframeDocument = instance.iframeWindow.document;
            const style = iframeDocument.createElement('style');
            style.textContent = `
        .DocumentContainer {
          background-color:rgb(255, 255, 255);
        }
        .HeaderItems {
          background-color: rgb(255, 255, 255);
        }
        .Icon {
          color: #b128bc;
        }
        .document {
          margin: 0 !important;
        }
        .view-header-border {
          display: none;
        }
        .Button:hover:enabled {
          background-color:rgba(178, 40, 188, 0.09) !important;
        }
        .Button.active {
          background-color:rgba(178, 40, 188, 0.09) !important;
        }
        .Button.active .Icon {
          color: #b128bc !important;
        }

        @keyframes invoiceBoundingPulse {
          0%, 100% {
            opacity: 0.45;
            transform: scale(1);
          }

          50% {
            opacity: 1;
            transform: scale(1.04);
          }
        }
      `;
            iframeDocument.head.appendChild(style);
            const { Core } = instance;
            Core.documentViewer.addEventListener('documentLoaded', () => {
                Core.setCanvasMultiplier(1.6);
            });
        });

        return () => {
            if (instance) {
                instance.UI.dispose();
            }
        };
    }, []);

    const prevBlobRef = useRef(null);

    useEffect(() => {
        if (viewer.current?.instance && blobUrl) {
            const instance = viewer.current.instance;
            const { UI, Core } = instance;

            const onDocumentLoaded = () => {
                setTimeout(() => {
                    Core.setCanvasMultiplier(1.6);
                    UI.setFitMode(UI.FitMode.FitWidth);
                }, 50);
            };

            Core.documentViewer.removeEventListener('documentLoaded', onDocumentLoaded);
            Core.documentViewer.addEventListener('documentLoaded', onDocumentLoaded);

            UI.loadDocument(blobUrl, { extension: 'pdf' });
        }

        prevBlobRef.current = blobUrl;

        return () => {
            if (prevBlobRef.current) {
                URL.revokeObjectURL(prevBlobRef.current);
            }
        };
    }, [blobUrl]);

    const handleDownload = () => {
        const { blobUrl: currentBlobUrl, fileName: currentFileName, onDownload: currentOnDownload } = viewerPropsRef.current;
        if (!currentBlobUrl) return;
        const link = document.createElement('a');
        link.href = currentBlobUrl;
        link.download = `${currentFileName}.pdf`;
        link.click();
        if (currentOnDownload) currentOnDownload();
    };

    const getPageElement = (iframeDocument, pageNumber) => {
        return iframeDocument.querySelector(`#pageContainer${pageNumber}`);
    };

    const showBoundingBox = (pageNumber, boundingPolygon, pageElementOverride = null) => {
        const instance = viewer.current?.instance;

        if (!instance || !pageNumber || !boundingPolygon) return false;

        const { Core } = instance;
        const iframeDocument = instance.iframeWindow.document;
        const scrollView = Core.documentViewer.getScrollViewElement?.();
        if (!scrollView) return false;

        const page = pageElementOverride || getPageElement(iframeDocument, pageNumber);

        if (!page) {
            return false;
        }

        const pageWidth = Core.documentViewer.getPageWidth(pageNumber);
        const pageHeight = Core.documentViewer.getPageHeight(pageNumber);

        const pageBounds = page.getBoundingClientRect();
        const rotation = Core.documentViewer.getRotation(pageNumber);

        const pdfIsLandscape = pageWidth > pageHeight;
        const renderedIsLandscape = pageBounds.width > pageBounds.height;
        const shouldBeLandscape = rotation % 2 === 0 ? pdfIsLandscape : !pdfIsLandscape;

        const isOrientationSwapped = renderedIsLandscape !== shouldBeLandscape;

        const azurePageWidth = (isOrientationSwapped ? pageHeight : pageWidth) / 72;
        const azurePageHeight = (isOrientationSwapped ? pageWidth : pageHeight) / 72;

        const azurePolygon = boundingPolygon
            .split(';')
            .map((point) => point.split(',').map(Number));

        const viewerPoints = azurePolygon.map(([x, y]) => {
            let normalizedX = x / azurePageWidth;
            let normalizedY = y / azurePageHeight;

            switch (rotation) {
                case 1:
                    [normalizedX, normalizedY] = [
                        1 - normalizedY,
                        normalizedX,
                    ];
                    break;

                case 2:
                    [normalizedX, normalizedY] = [
                        1 - normalizedX,
                        1 - normalizedY,
                    ];
                    break;

                case 3:
                    [normalizedX, normalizedY] = [
                        normalizedY,
                        1 - normalizedX,
                    ];
                    break;

                default:
                    break;
            }

            return {
                x: normalizedX * pageBounds.width,
                y: normalizedY * pageBounds.height,
            };
        });

        if (viewerPoints.length === 0) return false;

        const xs = viewerPoints.map((point) => point.x);
        const ys = viewerPoints.map((point) => point.y);

        const left = Math.min(...xs);
        const top = Math.min(...ys);
        const width = Math.max(...xs) - left;
        const height = Math.max(...ys) - top;

        const existingBox = iframeDocument.getElementById('invoice-bounding-box');

        if (existingBox) {
            existingBox.remove();
        }

        const box = iframeDocument.createElement('div');

        box.id = 'invoice-bounding-box';

        box.style.position = 'absolute';
        box.style.left = `${left - 3}px`;
        box.style.top = `${top - 3}px`;
        box.style.width = `${width + 6}px`;
        box.style.height = `${height + 6}px`;

        box.style.border = '2px solid #b128bc';
        box.style.backgroundColor = 'rgba(177, 40, 188, 0.08)';
        box.style.borderRadius = '4px';
        box.style.boxSizing = 'border-box';
        box.style.zIndex = '999999';
        box.style.pointerEvents = 'none';

        // Pulse animation
        box.style.animation = 'invoiceBoundingPulse 1.2s ease-in-out infinite';

        page.appendChild(box);

        // Scroll to the target page and then to the specific box area.
        const viewportLeft = scrollView.scrollLeft;
        const viewportTop = scrollView.scrollTop;
        const viewportRight = viewportLeft + scrollView.clientWidth;
        const viewportBottom = viewportTop + scrollView.clientHeight;

        const scrollRect = scrollView.getBoundingClientRect();

        const boxLeft = pageBounds.left - scrollRect.left + scrollView.scrollLeft + left;
        const boxTop = pageBounds.top - scrollRect.top + scrollView.scrollTop + top;
        const boxRight = boxLeft + width;
        const boxBottom = boxTop + height;

        const fullyVisible =
            boxLeft >= viewportLeft &&
            boxRight <= viewportRight &&
            boxTop >= viewportTop &&
            boxBottom <= viewportBottom;

        if (fullyVisible) {
            return true;
        }

        const targetX = boxLeft - scrollView.clientWidth / 2;
        const targetY = boxTop - scrollView.clientHeight / 2;
        const maxX = Math.max(0, scrollView.scrollWidth - scrollView.clientWidth);
        const maxY = Math.max(0, scrollView.scrollHeight - scrollView.clientHeight);

        scrollView.scrollTo({
            left: Math.max(0, Math.min(targetX, maxX)),
            top: Math.max(0, Math.min(targetY, maxY)),
            behavior: 'smooth',
        });

        return true;
    };

    useEffect(() => {
        const instance = viewer.current?.instance;
        const iframeDocument = instance?.iframeWindow?.document;

        if (!iframeDocument) return;

        const existingBox = iframeDocument.getElementById('invoice-bounding-box');
        if (existingBox) {
            existingBox.remove();
        }

        if (!highlightRegion) return;

        const pageNumber = Number(highlightRegion.pageNumber);
        const polygon = (highlightRegion.polygonPoints || [])
            .map(({ x, y }) => `${x},${y}`)
            .join(';');

        if (!Number.isFinite(pageNumber) || !polygon) return;

        const page = getPageElement(iframeDocument, pageNumber);

        if (page) {
            showBoundingBox(pageNumber, polygon, page);
        }
    }, [highlightRegion]);

    return (
        <>
            <div style={{ position: 'relative', height: '62.5vh' }}>
                <Box
                    className="webviewer"
                    ref={viewer}
                    sx={{ height: '100%', '& .HeaderItems': { border: '3px solid green' } }}
                />
            </div>
            {splitPDF &&
                <SplitPDF open={showSplitDialog} setOpen={setShowSplitDialog} blobUrl={viewerPropsRef.current.blobUrl} />
            }
        </>
    );
}
